/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text } from '@/shared/ui';
import { type ListingColumn } from '@/shared/ui/components/basic/listing-table';
import { FormattedDate } from '@/shared/ui/components/basic/formatted-date';
import { type Deal } from '@/services/requests/housien-services/DealsService';
import { DealStageBadge } from '../components/DealStageBadge';
import { DEAL_STAGES, type DealStage } from '../constants/deal-stages';

export type DealRow = Deal & { id: string };

/**
 * The pipeline rank of a stage — its index in the lifecycle order
 * (`new < contacted < visiting < negotiating < won < lost < paused`). The
 * stage column sorts on this rank so the pipeline reads in progress order,
 * not alphabetically (where "negotiating" would precede "new").
 */
function stageRank(stage: string): number {
  const idx = DEAL_STAGES.indexOf(stage as DealStage);
  return idx === -1 ? DEAL_STAGES.length : idx;
}

/**
 * Builds the column definitions for the deals listing table [Fase 6] --
 * converges DealsPage to ListingTable, replacing the hand-written table.
 *
 * @param t - Translator for column labels.
 * @returns Listing column definitions.
 */
export function buildDealColumns(t: (key: string) => string): ListingColumn<DealRow>[] {
  return [
    {
      key: 'client',
      label: t('columns.client'),
      width: '16%',
      sortable: true,
      sortAccessor: (d) => d.client_name ?? d.client_id ?? null,
      render: (d) => (
        <Text
          component="span"
          style={{ display: 'block', fontWeight: 'var(--font-semibold)' as unknown as number, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {d.client_name ?? d.client_id ?? '—'}
        </Text>
      ),
    },
    {
      key: 'property',
      label: t('columns.property'),
      width: '22%',
      sortable: true,
      sortAccessor: (d) => d.property_title ?? d.property_id ?? null,
      render: (d) => (
        <Text
          component="span"
          style={{ display: 'block', fontWeight: 'var(--font-semibold)' as unknown as number, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {d.property_title ?? d.property_id ?? '—'}
        </Text>
      ),
    },
    {
      key: 'type',
      label: t('columns.type'),
      width: '9%',
      sortable: true,
      sortAccessor: (d) => (d.deal_type === 'sale' ? t('types.sale') : t('types.rental')),
      render: (d) => <Text component="span">{d.deal_type === 'sale' ? t('types.sale') : t('types.rental')}</Text>,
    },
    {
      key: 'stage',
      label: t('columns.stage'),
      width: '15%',
      sortable: true,
      sortType: 'number',
      sortAccessor: (d) => stageRank(d.stage),
      render: (d) => <DealStageBadge stage={d.stage} />,
    },
    {
      key: 'value',
      label: t('columns.value'),
      width: '12%',
      sortable: true,
      sortType: 'number',
      sortAccessor: (d) => d.reference_value ?? null,
      render: (d) => (
        <Text component="span">
          {d.reference_value
            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(d.reference_value)
            : '—'}
        </Text>
      ),
    },
    {
      key: 'broker',
      label: t('columns.broker'),
      width: '16%',
      sortable: true,
      sortAccessor: (d) => d.broker_id ?? null,
      render: (d) => <Text component="span">{d.broker_id ?? '—'}</Text>,
    },
    {
      key: 'updated',
      label: t('columns.updated'),
      width: '10%',
      sortable: true,
      sortType: 'date',
      sortAccessor: (d) => d.updated_at ?? null,
      render: (d) => (d.updated_at ? <FormattedDate value={d.updated_at} /> : <Text component="span">—</Text>),
    },
  ];
}
